/* eslint-disable guard-for-in */
/* eslint-disable no-restricted-syntax */
import Vue from 'vue';
import axios from 'axios';
import { i18n } from '@/plugins/I18n.js';
import { serialize } from 'object-to-formdata';

function checkBan(response) {
  const error = response.errors.find(e => e.code === 'ERROR_DEVICE_BANNED');
  if (!response.success && error) {
    if (Vue.router.currentRoute.name !== 'ban') {
      Vue.router.push({
        name: 'ban',
        params: {
          reason: response.data,
          heading: error.translated,
        },
      });
    }
  }
}

function checkPlusRequired(response) {
  const error = response.errors.find(e => e.code === 'ERROR_PLUS_REQUIRED');
  if (!error || !response.data) {
    return;
  }

  Vue.router.push({
    path: response.data,
  });

  throw new Error(error.code);
}

// function showMaintenanceError() {
//   Vue.prototype.$alert('appdb is in maintenance mode, please check <a href="https://status.dbservices.to">status page</a> for information.', {
//     okText: 'Refresh',
//   }).finally(() => window.location.reload());
// }

class Api {
  domain = 'https://api.dbservices.to/v1.7/';

  async call(action, data, handleErrors = true, returnFullResponse = false, config = {}) {
    let response = await axios({
      method: 'GET',
      url: `${this.domain}${action}/`,
      params: {
        lang: Vue.store.getters.lang,
        lt: Vue.store.getters.lt,
        rc: Vue.store.getters.ref,
        ...data,
        brand: process.env.BRAND_CONFIG.name,
      },
      withCredentials: true,
      ...config,
    });

    response = response.data;

    checkBan(response);
    checkPlusRequired(response);

    if (!handleErrors) {
      return response;
    }

    if (!response.success) {
      if (response.errors.length && response.errors[0].code === 'ERROR_MAINTENANCE_MODE') {
        // eslint-disable-next-line no-restricted-globals
        location.href = `/maintenance#${Vue.store.state.theme}`;
        return null;
      }

      Vue.notify({
        type: 'error',
        text: response.errors[0].translated + (typeof response.data === 'string' && response.data ? `. ${response.data}` : ''),
      });

      throw new Error(response.errors[0].code);
    }

    if (returnFullResponse) {
      return response;
    }

    return response.data;
  }

  async post(action, data, handleErrors = true, config) {
    const form = serialize(data);

    for (const key of form.keys()) {
      if (!form.get(key)) {
        form.delete(key);
      }
    }

    let link = `${this.domain}${action}/?lt=${Vue.store.getters.lt}&lang=${Vue.store.getters.lang}&brand=${process.env.BRAND_CONFIG.name}`;
    if (Vue.store.getters.refCode) {
      link += `&ref_code=${Vue.store.getters.refCode}`;
    }
    const response = await axios.post(link, form, config);

    const result = response.data;

    checkBan(result);

    if (handleErrors) {
      if (!result.success) {
        Vue.notify({
          type: 'error',
          text: result.errors[0].translated + (typeof result.data === 'string' && result.data ? `. ${result.data}` : ''),
        });

        throw new Error(result.errors[0].code);
      }

      return result.data;
    }

    return result;
  }

  async search({
    type,
    price = 0,
    genre = 0,
    dev = 0,
    order = 'added',
    page = 1,
    q = '',
  }) {
    return this.call('search', {
      type,
      price,
      genre,
      dev,
      order,
      length: 25,
      start: (page - 1) * 25,
      q,
    });
  }

  searchIndex(filters = {}, config) {
    return this.call('search_index', filters, undefined, undefined, config);
  }

  async getGenres() {
    return this.call('list_genres');
  }

  async getNews({
    limit = 3,
    id = null,
  } = {}) {
    return this.call('get_pages', {
      category: 'news',
      length: limit,
      id,
    });
  }

  async getArticle(id) {
    return this.call('get_pages', {
      category: 'articles',
      id,
    });
  }

  async getAppDetails({
    type,
    trackid,
  }) {
    return this.call('search', {
      type,
      trackid,
    });
  }

  async getLinks({
    type,
    trackid,
  }) {
    return this.call('get_links', {
      type,
      trackids: [trackid],
    }, false);
  }

  async getRequests(page, filter = {}) {
    return this.call('get_publish_requests', {
      type: 'ios',
      length: 25,
      start: (page - 1) * 25,
      ...filter,
    });
  }

  async link({
    email = '',
    code = '',
    type = 'new',
  }, handleErrors = false) {
    return this.call('link', {
      email,
      link_code: code,
      type,
    }, handleErrors);
  }

  getTVLinkCode({ email }) {
    return this.call('get_tv_link_code', { email });
  }

  async getDevices(handleErrors = false) {
    return this.call('get_all_devices', {}, handleErrors);
  }

  async getLinkCode() {
    return this.call('get_link_code');
  }

  async getDeviceStatus() {
    return this.call('get_status');
  }

  async clearCommands() {
    return this.call('clear');
  }

  async getUpdates() {
    const loc = window.location.href;
    const get = (ticket, resolve, reject) => {
      this.call('get_updates', { t: ticket }, false)
        .then((response) => {
          if (response.success) {
            resolve(response.data);
            return;
          }
          if (response.errors.some(e => e.code === 'ERROR_NOT_READY')) {
            const timeout = setTimeout(() => {
              get(ticket, resolve, reject);
            }, 2000);
            if (loc !== window.location.href) {
              clearTimeout(timeout);
            }
            return;
          }

          reject(response.errors);
        });
    };
    return this.call('get_update_ticket')
      .then(ticket => new Promise((resolve, reject) => get(ticket, resolve, reject)));
  }

  async getConfiguration(handleErrors = true) {
    return this.call('get_configuration', {}, handleErrors);
  }

  async saveConfiguration(config) {
    const params = {};
    Object.keys(config)
      .forEach((key) => {
        params[`params[${key}]`] = config[key];
      });

    const getCodeAndSave = () => Vue.prototype.$prompt(i18n.t('ENTER_OTP'))
      .then((code) => {
        params['params[2fa_code]'] = code;
        return this.post('configure', params);
      })
      .catch((e) => {
        if (e && e.message === 'ERROR_2FA_CODE_IS_WRONG') {
          return getCodeAndSave();
        }

        throw e;
      });

    return this.post('configure', params)
      .catch((e) => {
        if (e && e.message === 'ERROR_2FA_CODE_IS_REQUIRED') {
          return getCodeAndSave();
        }

        throw e;
      });
  }

  async installApp({
    id,
    type,
    features,
    isFromRepo,
    name,
    link,
    image,
    version,
    installationTicket,
  }) {
    const options = {
      enable_features: features,
      type,
      installation_ticket: installationTicket,
    };


    if (!isFromRepo) {
      options.id = id;
    } else {
      options.bundle_id = id;
      options.name = name;
      options.link = link;
      options.image = image;
      options.version = version;
    }

    return this.post('install', options, false)
      .then((resp) => {
        if (!resp.success) {
          // eslint-disable-next-line no-throw-literal
          throw [resp.errors[0].code, resp.errors[0].translated];
        }

        Vue.store.dispatch('getConfig');
      });
  }

  async reportLink({
    id,
    type,
    reason,
  }) {
    return this.call('report', {
      id,
      type,
      reason,
    });
  }

  uploadFile({ type, file }) {
    return this.post('upload_file', {
      type,
      file,
    });
  }

  async uploadApp({
    file,
    id,
  }, progressCb) {
    return this.post('add_ipa', {
      ipa: file,
      job_id: id,
    }, false, { onUploadProgress: progressCb });
  }

  async editIpaMetadata(metadata) {
    return this.post('edit_ipa_metadata', metadata, false);
  }

  async uploadAppByUrl({
    url,
    id,
  }) {
    return this.post('add_ipa', {
      url,
      job_id: id,
    });
  }

  async getAppsJobs() {
    return this.call('get_ipa_analyze_jobs');
  }

  async deleteAppJob(id) {
    return this.call('delete_ipa_analyze_job', { id });
  }

  getIpas(filters = {}, config) {
    return this.call('get_ipas', filters, undefined, undefined, config);
  }

  async deleteIpa(id) {
    return this.call('delete_ipa', { id });
  }

  getInstallationHistory() {
    return this.call('get_installation_history');
  }

  async getPlusPurchaseOptions() {
    return this.call('get_plus_purchase_options');
  }

  async getLanguages() {
    return this.call('get_languages');
  }

  async requestApp(url, version) {
    return this.call('create_publish_request', {
      type: 'ios',
      url,
      version,
    });
  }

  async setPublishRequestStatus({
    status, id, uoid, type,
  }) {
    return this.post('set_publish_request_status', {
      status,
      id,
      universal_object_identifier: uoid,
      type,
    });
  }

  async getRedirectParams(ticket) {
    return this.call('process_redirect', {
      t: ticket,
    });
  }

  async getRedirectLink(ticket) {
    return this.call('process_redirect', {
      rt: ticket,
    });
  }

  async getPromotions() {
    return this.call('get_promotions');
  }

  async getStats() {
    return this.call('get_stats');
  }

  async purchaseWithBTC({
    mode,
    txid,
    ticket,
  }) {
    return this.call('bitcoin_purchase', {
      mode,
      txid,
      purchase_ticket: ticket,
    });
  }

  async unlinkDevice(lt) {
    return this.call('unlink', {
      device_link_token: lt,
    });
  }

  async getInstallFeatures() {
    return this.call('get_features');
  }

  cancelPlusSubscription() {
    return this.call('cancel_plus_subscription');
  }

  getSubscriptions() {
    return this.call('get_subscriptions');
  }

  getPurchases() {
    return this.call('get_purchases');
  }

  editPlusDevAccount({
    appleid,
    password,
    isSMSAuth,
    otp,
    status,
    sharedWithEmails,
  }) {
    return this.call('edit_plus_dev_account', {
      appleid,
      password,
      is_sms_auth: isSMSAuth,
      otp,
      status,
      shared_with_emails: sharedWithEmails,
    });
  }

  deletePlusDevAccount() {
    return this.call('delete_plus_dev_account');
  }

  getPlusDevAccountArchive() {
    return this.call('get_plus_dev_account_archive');
  }

  getDevCredentialsProviders() {
    return this.call('get_dev_credentials_providers');
  }

  getEnterpriseCertificates() {
    return this.call('get_enterprise_certs');
  }

  addRepo(url) {
    return this.post('edit_repo', {
      url,
    }, false);
  }

  editRepo({
    url,
    id,
    isPublic,
  }) {
    return this.post('edit_repo', {
      url,
      id,
      is_public: isPublic,
    });
  }

  deleteRepo(id) {
    return this.call('delete_repo', {
      id,
    });
  }

  getRepos(isPublic) {
    return this.call('get_repos', {
      is_public: isPublic,
    });
  }

  getRepo(id) {
    return this.call('get_repos', {
      id,
    }, false);
  }

  getDylibs() {
    return this.call('get_dylibs');
  }

  addDylib({ file, url } = {}) {
    return this.post('add_dylib', {
      dylib: file,
      url,
    });
  }

  deleteDylib(name) {
    return this.post('delete_dylib', {
      name,
    });
  }

  getSideloadingOptions(type) {
    return this.call('get_sideloading_options', {
      type,
    });
  }

  getP2POffers({ currency }) {
    return this.call('get_p2p_offers', { currency });
  }

  editP2POffer({ id, operation }) {
    return this.post('edit_p2p_offer', {
      id,
      operation,
    });
  }

  editPlusDevAccountP2PSettings({
    isP2P,
    p2pPrice,
    p2pName,
    p2pPaypalEmail,
    p2pCurrency,
    p2pManualInstructions,
  }) {
    return this.post('edit_plus_dev_account_p2p_settings', {
      is_p2p: isP2P,
      p2p_price: p2pPrice,
      p2p_name: p2pName,
      p2p_paypal_email: p2pPaypalEmail,
      p2p_currency: p2pCurrency,
      p2p_manual_instructions: p2pManualInstructions,
    });
  }

  getP2PPurchases() {
    return this.call('get_p2p_purchases', null, false);
  }

  banunbanDevice({
    udid,
    reason,
  }) {
    return this.call('banunban_device', { udid, reason });
  }

  changeEmail({ newEmail, ticket, code }) {
    return this.call('change_email', { new_email: newEmail, ticket, code });
  }

  universalGateway({ universalObjectId, id, silent }) {
    return this.call('universal_gateway', { universal_object_identifier: universalObjectId, id }, !silent);
  }

  importToLibrary(universalObjectId) {
    return this.call('import_to_library', { universal_object_identifier: universalObjectId });
  }

  getCustomerProfile() {
    return this.call('get_customer_profile', undefined, false);
  }
}

const plugin = {
  install() {
    Vue.prototype.$api = new Api();
  },
};

export default plugin;
